import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

// Order matters: the first three are the platforms UAE audience actually
// shares on, the rest are secondary.
const SHARE_TARGETS = [
  { id: 'whatsapp', label: 'Share on WhatsApp', icon: 'eva:whatsapp-fill', color: '#25D366' },
  { id: 'linkedin', label: 'Share on LinkedIn', icon: 'eva:linkedin-fill', color: '#0A66C2' },
  { id: 'facebook', label: 'Share on Facebook', icon: 'eva:facebook-fill', color: '#1877F2' },
  { id: 'twitter', label: 'Share on X', icon: 'eva:twitter-fill', color: '#000000' },
  { id: 'email', label: 'Share by email', icon: 'eva:email-fill', color: '#DF3E30' },
];

const buildShareUrl = (id, { url, title }) => {
  switch (id) {
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(title)}`;
    case 'email':
      return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
    default:
      return url;
  }
};

const ALIGN_MAP = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

// ----------------------------------------------------------------------

// One sharing row used under the byline and again after the article, so a
// reader can share without scrolling back up.
export default function BlogShareButtons({ post, align = 'left', sx }) {
  const [copied, setCopied] = useState(false);

  const url = typeof window !== 'undefined' ? window.location.href : '';
  const { title, share } = post;

  useEffect(() => {
    if (!copied) {
      return undefined;
    }

    const timer = window.setTimeout(() => setCopied(false), 2000);

    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.position = 'absolute';
        field.style.left = '-9999px';
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        document.body.removeChild(field);
      }

      setCopied(true);
    } catch (error) {
      setCopied(false);
    }
  };

  const buttonSx = (color) => ({
    width: 40,
    height: 40,
    color: 'text.secondary',
    border: (theme) => `1px solid ${theme.palette.divider}`,
    '@media (hover: hover)': {
      '&:hover': {
        color: 'common.white',
        bgcolor: color,
        borderColor: color,
      },
    },
  });

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ flexWrap: 'wrap', alignItems: 'center', justifyContent: ALIGN_MAP[align] ?? ALIGN_MAP.left, ...sx }}
    >
      <Typography variant="overline" sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 1 }}>
        Share
      </Typography>

      {SHARE_TARGETS.map((target) => (
        <Tooltip key={target.id} title={target.label}>
          <Button
            component="a"
            href={buildShareUrl(target.id, { url, title })}
            target={target.id === 'email' ? undefined : '_blank'}
            rel="noopener noreferrer"
            aria-label={target.label}
            sx={buttonSx(target.color)}
          >
            <Iconify icon={target.icon} sx={{ width: 20, height: 20 }} />
          </Button>
        </Tooltip>
      ))}

      <Tooltip title={copied ? 'Link copied' : 'Copy link'}>
        <Button aria-label="Copy link" onClick={handleCopy} sx={buttonSx('#DF3E30')}>
          <Iconify icon={copied ? 'eva:checkmark-circle-fill' : 'eva:link-2-fill'} sx={{ width: 20, height: 20 }} />
        </Button>
      </Tooltip>

      {share > 0 && (
        <Box sx={{ ml: { xs: 0, sm: 1 }, color: 'text.disabled', fontSize: 13 }}>
          {share} {share === 1 ? 'share' : 'shares'}
        </Box>
      )}
    </Stack>
  );
}

BlogShareButtons.propTypes = {
  post: PropTypes.object.isRequired,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  sx: PropTypes.object,
};
